import type { CSSProperties } from 'react'

/**
 * One joined segmented control (`.seg` in global.css): a single bordered pill whose segments are
 * the buckets. Each segment may carry its own colour (text when idle, fill when selected) via the
 * --seg-color custom property; colourless segments use the accent. The inline style is
 * load-bearing beyond the colour itself - global.css keys the on-accent text-contrast rule off
 * `:not([style*="--seg-color"])`, so a segment that sets its colour any other way loses it.
 *
 * A segment whose count is 0 is DISABLED: with cross-filtered counts a zero means "this bucket
 * holds nothing under the other filters", so clicking it could only ever empty the table. The
 * selected segment stays enabled at zero - it has to remain focusable to be navigated away from.
 *
 * Extracted from pages/Providers.tsx, which was its only call site. It is a toggle group
 * (`role="group"` + `aria-pressed`), NOT a radiogroup, and relies on native button semantics for
 * keyboard support rather than roving tabindex.
 */
export function SegmentedFilter<T extends string>({ label, value, onChange, options, emptyNoun = 'findings' }: {
  label: string
  value: T
  onChange: (v: T) => void
  options: { key: T; label: string; count: number; color?: string }[]
  /** Noun used in the disabled segment's tooltip. */
  emptyNoun?: string
}) {
  return (
    <div role="group" aria-label={label} className="seg">
      {options.map((o) => {
        const empty = o.count === 0 && value !== o.key
        return (
          <button
            key={o.key}
            type="button"
            className="seg-btn"
            aria-pressed={value === o.key}
            disabled={empty}
            title={empty ? `No ${o.label.toLowerCase()} ${emptyNoun} under the current filters` : undefined}
            onClick={() => onChange(o.key)}
            style={o.color ? ({ '--seg-color': o.color } as CSSProperties) : undefined}
          >
            {o.label} ({o.count})
          </button>
        )
      })}
    </div>
  )
}
